import classNames from 'classnames'
import React from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'

import Locale from 'data/locale'
import './App.scss'

import Store from 'store'
import Session from 'store/session'

import EditingContext from 'store/editingContext'
import { ModifierContext } from 'ui/modifiers'
import OverlayRoot, { OverlayContext } from 'ui/utils/OverlayManager'

import { useDrop } from 'react-dnd'
import Tabs from 'ui/tabs'

import DragLayer from 'components/DragLayer'
//import { LoginBar } from 'components/account/LoginLine';
import { faFile } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BuildInfoLevel,
  getStoredValue,
  HasPTR,
  PD2,
  PTR,
  setStoredValue
} from 'common'
import { LoginBar } from 'components/account/LoginLine'
import BuildInfo from 'components/buildinfo/BuildInfo'
import CalculatorPane from 'components/calculator/CalculatorPane'
import CharacterPane from 'components/character/CharacterPane'
import EquipmentPane from 'components/equipment/EquipmentPane'
import { IconLibrary } from 'components/IconLibrary'
import ImportPane from 'components/import/ImportPane'
import { ProfileUpdateButton } from 'components/import/Profiles'
import { ImportTracker } from 'components/importTracker'
import Paperdoll from 'components/paperdoll/Paperdoll'
import ProfileList from 'components/paperdoll/ProfileList'
import Stats from 'components/stats/Stats'
import GameContext, { GameVersions } from 'gamectx'
import { parseFile } from 'logic/binary'
import { stateAddProfile, stateFix, stateIsEmpty } from 'logic/stateReducers'
import Dialog from 'ui/dialog'
import { DndProviderWrapper } from 'ui/dragdrop'
import Tooltip from 'ui/tooltip'
import Overlay, { ErrorPopup } from 'ui/utils/Overlay'

const _L = Locale('title')

const tabComponents = {
  buildinfo: BuildInfo,
  equipment: EquipmentPane,
  character: CharacterPane,
  calculator: CalculatorPane,
  import: ImportPane
}

function NewConfirmation({ onResolve, onClose }) {
  return (
    <Dialog title={_L`New profile`} modal onClose={onClose}>
      <Dialog.Content>
        <p>{_L`Delete all sets and start from a blank profile?`}</p>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve(true)}>{_L`Yes`}</button>
        <button onClick={() => onResolve(false)}>{_L`No`}</button>
      </Dialog.Footer>
    </Dialog>
  )
}

function ProfileNewButton() {
  const isEmpty = Store.useSelector((state) => stateIsEmpty(state))
  const imported = Store.useSelector((state) => state.imported)
  const dispatch = Store.useDispatch()
  const overlays = React.useContext(OverlayContext)
  const onClick = React.useCallback(async () => {
    if (imported !== true && !(await overlays.async(<NewConfirmation />)))
      return
    dispatch.setState(stateFix())
  }, [dispatch, overlays, imported])
  if (isEmpty) return null
  return (
    <Tooltip tooltip={_L`New profile`}>
      <FontAwesomeIcon icon={faFile} onClick={onClick} />
    </Tooltip>
  )
}

const Header = React.memo(function Header({ gameVersion, onSetVersion }) {
  /*const [locale, setLocale] = React.useState(() => getStoredValue("locale", "enUS"));
  const onChangeLocale = React.useCallback(value => {
    setLocale(value.id);
    setStoredValue("locale", value.id);
    setTimeout(() => window.location.reload());
  }, []);*/

  return (
    <div className="d2p-Header">
      <div className="d2p-logo" />
      <div className="d2p-toolbar">
        <form>
          <div>
            <label>
              <input
                type="radio"
                checked={gameVersion === 'lod'}
                onChange={() => onSetVersion('lod')}
              />{' '}
              LoD
            </label>
            <label>
              <input
                type="radio"
                checked={gameVersion === 'd2r'}
                onChange={() => onSetVersion('d2r')}
              />{' '}
              D2R
            </label>
            {HasPTR && (
              <label>
                <input
                  type="radio"
                  checked={gameVersion === 'ptr'}
                  onChange={() => onSetVersion('ptr')}
                />{' '}
                PTR
              </label>
            )}
            {/* <label><input type="radio" checked={gameVersion === 'pd2'} onChange={() => onSetVersion('pd2')}/> PD2</label> */}
          </div>
        </form>
        <div className="d2p-extra-buttons">
          <ProfileNewButton />
          <ProfileUpdateButton />
        </div>
      </div>
      <div className="d2p-content"></div>
      <div className="d2p-login">
        <LoginBar />
      </div>
    </div>
  )
})

const App = React.memo(function App({ loading }) {
  const overlays = React.useContext(OverlayContext)
  const [spinner, setSpinner] = EditingContext.useState('spinner', 0)

  const [gameVersion, setGameVersion] = React.useState(() =>
    PD2 ? 'pd2' : PTR ? 'ptr' : getStoredValue('d2p-gameVersion', 'd2r')
  )
  const onSetVersion = React.useCallback(
    (newVersion) => {
      if (newVersion === gameVersion || !GameVersions[newVersion]) return
      if (GameVersions[gameVersion]) {
        const oldGfx = GameVersions[gameVersion].gfx
        const newGfx = GameVersions[newVersion].gfx
        if (oldGfx !== newGfx) {
          oldGfx?.clearCache()
          newGfx?.clearCache()
        }
      }
      if (!GameVersions[newVersion].suffix)
        setStoredValue('d2p-gameVersion', newVersion)
      setGameVersion(newVersion)
      //if (GameVersions[newVersion].suffix || GameVersions[gameVersion].suffix) {
      window.location.pathname = window.location.pathname.replace(
        /d2planner(-\w+)?/,
        GameVersions[newVersion].suffix
          ? `d2planner-${newVersion}`
          : 'd2planner'
      )
      //}
    },
    [gameVersion]
  )

  const [tab, setTab] = React.useState('equipment')

  const userLevel = Session.useSelector((store) => store.userLevel || 0)
  const hasBuildInfo = Store.useProfile((profile) => !!profile.buildinfo?.text)
  const showBuildInfo = userLevel >= BuildInfoLevel || hasBuildInfo

  const title = Store.useSelector((state) => state.name)
  React.useEffect(() => {
    const _Lt = Locale('title')
    const pageTitle = _Lt`Diablo II Character Planner`
    if (title) {
      document.title = title + ' - ' + pageTitle
    } else {
      document.title = pageTitle
    }
  }, [title])

  const fireOnce = React.useRef(false)
  React.useEffect(() => {
    if (fireOnce.current) return
    fireOnce.current = true
    if (loading) {
      setSpinner((spin) => spin + 1)
      loading
        .then((error) => {
          if (error) {
            overlays.async(<ErrorPopup message={error} />)
          }
        })
        .finally(() => setSpinner((spin) => spin - 1))
    }
  }, [loading, setSpinner, overlays])

  const tabList = React.useMemo(() => {
    const tabs = [
      { id: 'buildinfo', name: _L`Guide` },
      { id: 'equipment', name: _L`Equipment` },
      { id: 'character', name: _L`Character` },
      { id: 'calculator', name: _L`Calculations` },
      { id: 'import', name: _L`Save/Load` }
    ]
    if (!showBuildInfo) {
      tabs.splice(0, 1)
    }
    return tabs
  }, [showBuildInfo])

  const editingCallback = React.useCallback(
    ({ type }) => {
      switch (type) {
        case 'buildinfo':
          if (showBuildInfo) {
            setTab('buildinfo')
            return true
          }
          break
        case 'item':
        case 'slot':
          setTab('equipment')
          break
        case 'equipment':
          setTab('equipment')
          return true
        // no default
      }
    },
    [showBuildInfo]
  )
  EditingContext.useHandler(editingCallback)

  React.useLayoutEffect(() => {
    if (!showBuildInfo && tab === 'buildinfo') {
      setTab('equipment')
    }
  }, [tab, showBuildInfo])

  const dispatch = Store.useDispatch()
  const [{ isDropping }, dropRef] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item) {
        const file = item.files?.[0]
        if (file?.name.match(/\.d2s$/i)) {
          parseFile(file).then(
            (result) =>
              dispatch.setState((state) =>
                stateAddProfile(state, result.profile, result.items)
              ),
            (error) =>
              overlays.async(
                <ErrorPopup
                  title={_L`Failed to parse file`}
                  message={error.message}
                />
              )
          )
        }
      },
      collect: (monitor) => ({
        isDropping: monitor.isOver() && monitor.canDrop()
      })
    }),
    [dispatch]
  )

  const TabComponent = tabList.find((t) => t.id === tab)
    ? tabComponents[tab]
    : null

  const gameCtx = GameVersions[gameVersion] || GameVersions.d2r

  return (
    <GameContext.Provider value={gameCtx}>
      <IconLibrary>
        <div
          className={classNames('d2planner-App', {
            'd2p-dropping': isDropping,
            'd2p-game-hd': gameCtx.gfx.hd
          })}
          ref={dropRef}>
          <Header gameVersion={gameVersion} onSetVersion={onSetVersion} />
          <div className="d2p-Body">
            <div className="d2p-LeftColumn">
              <Paperdoll />
              <div style={{ flex: '0 0 10px' }} />
              <ProfileList />
            </div>
            <Tabs
              tabs={tabList}
              value={tab}
              onChange={setTab}
              className="d2p-MainColumn">
              {!!TabComponent && <TabComponent />}
            </Tabs>
            <div className="d2p-RightColumn">
              <Stats />
            </div>
          </div>
          <DragLayer />
          {spinner > 0 && (
            <Overlay>
              <div className="d2p-Spinner" />
            </Overlay>
          )}
        </div>
      </IconLibrary>
    </GameContext.Provider>
  )
})

export default function AppContext({ session, loading, editingContext }) {
  return (
    <React.Fragment>
      {/* <AdUnit id="d2planner-left" className="d2planner-ad-left"/> */}
      <DndProviderWrapper>
        <Store.Provider store={Store.instance}>
          <Session.Provider store={session}>
            <EditingContext.Provider context={editingContext}>
              <ModifierContext>
                <OverlayRoot>
                  <ImportTracker>
                    <App loading={loading} />
                  </ImportTracker>
                </OverlayRoot>
              </ModifierContext>
            </EditingContext.Provider>
          </Session.Provider>
        </Store.Provider>
      </DndProviderWrapper>
      {/* <AdUnit id="d2planner-right" className="d2planner-ad-right"/> */}
    </React.Fragment>
  )
}
